import {useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomPopover, {atomPopoverPositions, atomPopoverTriggers} from '@s-ui/react-atom-popover'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'

const BASE_CLASS = 'mt-OnboardingCoachmark'

const OnboardingCoachmark = ({
  badge,
  children,
  delay = 0,
  isEnabled = false,
  onClose = () => {},
  placement = atomPopoverPositions.BOTTOM,
  text,
  title
}) => {
  // Always starts closed, even when delay is 0 — AtomPopover's isVisible flipping true
  // on the component's very first render used to crash reactstrap's lazy-loaded
  // TooltipPopoverWrapper with "Cannot read properties of undefined (reading 'length')"
  // on a second mount within the same session (confirmed: a fresh instance crashes the
  // same way as a reused one, so it isn't about remounting — it's isOpen=true racing the
  // vendor's own target-ref attachment on whichever render pass it resolves in). Setting
  // it via an effect (setTimeout, even 0ms) defers "visible" to a macrotask after the
  // initial commit has fully flushed, which the vendor's mount sequence needs.
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isEnabled) return

    const timeoutId = setTimeout(() => setIsOpen(true), delay)

    return () => clearTimeout(timeoutId)
  }, [isEnabled, delay])

  const handleClose = () => {
    setIsOpen(false)
    onClose()
  }

  return (
    <AtomPopover
      closeIcon={
        <span className={`${BASE_CLASS}-iconClose`}>
          <IconClose size={ATOM_ICON_SIZES.medium} />
        </span>
      }
      content={
        <div className={`${BASE_CLASS}-content`}>
          {badge && <span className={`${BASE_CLASS}-contentBadge`}>{badge}</span>}
          {title && <p className={`${BASE_CLASS}-contentTitle`}>{title}</p>}
          {text && <p className={`${BASE_CLASS}-contentText`}>{text}</p>}
        </div>
      }
      hideArrow={false}
      isVisible={isEnabled && isOpen}
      onClose={handleClose}
      placement={placement}
      trigger={atomPopoverTriggers.MANUAL}
      type="dark"
    >
      <div className={`${BASE_CLASS}-target`}>{children}</div>
    </AtomPopover>
  )
}

export {atomPopoverPositions as onboardingCoachmarkPlacements}

OnboardingCoachmark.displayName = 'OnboardingCoachmark'
OnboardingCoachmark.propTypes = {
  badge: PropTypes.string,
  children: PropTypes.node,
  delay: PropTypes.number,
  isEnabled: PropTypes.bool,
  onClose: PropTypes.func,
  placement: PropTypes.string,
  text: PropTypes.string,
  title: PropTypes.string
}

export default OnboardingCoachmark
